import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { access, apiErrorSchema } from './schemas/common.js';
import { appUserSchema, userChannelSchema, userRoleSchema } from './schemas/domain.js';
const c = initContract();
/** Telegram ids are numeric but exceed 2^53, so they travel as digit strings. */
export const telegramIdSchema = z.string().regex(/^\d{5,20}$/, 'a Telegram user id is 5-20 digits');
const byId = z.object({ id: z.uuid() });
/** Source ids a user may read — `samsara:acme`. Which exist is the use-case's rule. */
const sourceAccessSchema = z.array(z.string().min(1).max(120)).max(200);
/**
 * Refused because of another record: this is the last enabled admin, or the
 * email or Telegram id already belongs to somebody else.
 */
export const userConflictSchema = z.object({
    error: z.enum(['last_admin', 'duplicate']),
    message: z.string(),
});
/**
 * Shapes only. Which identifier each channel needs (Telegram → telegramId,
 * web → email) is the use-case's rule, answered as a 400 naming the field.
 */
const newUserSchema = z.object({
    name: z.string().min(1).max(80),
    email: z.string().max(254).optional(),
    telegramId: telegramIdSchema.optional(),
    telegramUsername: z.string().max(33).optional(),
    role: userRoleSchema,
    channels: z.array(userChannelSchema).min(1),
    sourceAccess: sourceAccessSchema.nullable().optional(),
});
/** `null` clears an optional field. */
const userPatchSchema = z.object({
    name: z.string().min(1).max(80).optional(),
    email: z.string().max(254).nullable().optional(),
    telegramId: telegramIdSchema.nullable().optional(),
    telegramUsername: z.string().max(33).nullable().optional(),
    role: userRoleSchema.optional(),
    channels: z.array(userChannelSchema).min(1).optional(),
    /** `null`: every source, including ones connected later. */
    sourceAccess: sourceAccessSchema.nullable().optional(),
    enabled: z.boolean().optional(),
});
/**
 * The one list both doors read. `last_admin` is its own answer because the
 * alternative — a deployment with nobody able to grant access — is only
 * recoverable with an environment variable and a restart.
 */
export const usersContract = c.router({
    list: {
        method: 'GET',
        metadata: access('admin'),
        path: '/users',
        responses: { 200: z.object({ users: z.array(appUserSchema) }) },
    },
    add: {
        method: 'POST',
        metadata: access('admin'),
        path: '/users',
        body: newUserSchema,
        responses: {
            201: z.object({ user: appUserSchema }),
            400: apiErrorSchema,
            409: userConflictSchema,
        },
    },
    update: {
        method: 'PATCH',
        metadata: access('admin'),
        path: '/users/:id',
        pathParams: byId,
        body: userPatchSchema,
        responses: {
            200: z.object({ user: appUserSchema }),
            400: apiErrorSchema,
            404: apiErrorSchema,
            409: userConflictSchema,
        },
    },
    remove: {
        method: 'DELETE',
        metadata: access('admin'),
        path: '/users/:id',
        pathParams: byId,
        body: z.object({}),
        responses: {
            200: z.object({ user: appUserSchema }),
            404: apiErrorSchema,
            409: userConflictSchema,
        },
    },
});
//# sourceMappingURL=users.contract.js.map